import { useEffect } from 'react';
import { useTerminalStore } from './stores/terminal.store.js';
import { BootSequence }   from './components/layout/BootSequence.js';
import { TopBar }         from './components/layout/TopBar.js';
import { DynamicGrid }    from './components/layout/DynamicGrid.js';
import { HUDBackground }  from './components/layout/HUDBackground.js';
import { NewsTicker }     from './components/layout/NewsTicker.js';
import { AlertSettings }  from './components/alerts/AlertSettings.js';
import { useWebSocket }   from './hooks/useWebSocket.js';
import { usePricePoller } from './hooks/usePriceData.js';
import { useAlerts }      from './hooks/useAlerts.js';
import { useNews }        from './hooks/useNews.js';
import { useTradingEngine } from './hooks/useTrading.js';
import { useAutoTrade }     from './hooks/useAutoTrade.js';
import { useWalletBalancePoller }  from './hooks/useWalletBalancePoller.js';
import { useHLPositionSync }       from './hooks/useHLPositionSync.js';
import { usePriceAlertEngine }     from './hooks/usePriceAlertEngine.js';
import { useDbSync }               from './hooks/useDbSync.js';

// ─── Data + Side-effect Layer ─────────────────────────────────────────────────

function DataLayer() {
  useDbSync();                 // load from DB on boot, sync state changes back
  useWebSocket();
  usePricePoller();
  useAlerts();
  useNews();
  useTradingEngine();          // paper trading tick — exactly ONE interval
  useAutoTrade();              // auto-execute paper trades on UT Bot signals
  useWalletBalancePoller();    // poll live wallet balance every 30s
  useHLPositionSync();         // sync HL positions every 10s in live mode
  usePriceAlertEngine();       // fire price alerts on price updates
  return null;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const isBootComplete    = useTerminalStore(s => s.isBootComplete);
  const theme             = useTerminalStore(s => s.theme);
  const showAlertSettings = useTerminalStore(s => s.showAlertSettings);
  const uiScale           = useTerminalStore(s => s.uiScale);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Apply zoom via document root — unlike transform:scale, this keeps mouse
  // coordinates aligned so react-grid-layout drag works correctly at all scales.
  useEffect(() => {
    document.documentElement.style.zoom = String(uiScale);
    return () => { document.documentElement.style.zoom = ''; };
  }, [uiScale]);

  return (
    <>
      <DataLayer />
      {!isBootComplete && <BootSequence />}

      <div
        style={{ position: 'fixed', inset: 0, overflow: 'hidden' }}
        className={`flex flex-col transition-opacity duration-700 relative ${
          isBootComplete ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Animated HUD background (z-0) */}
        <HUDBackground />

        {/* All UI sits above the background */}
        <div className="relative z-10 flex flex-col h-full">
          <TopBar />
          <DynamicGrid />
          <NewsTicker />
        </div>

        {/* Alert settings drawer */}
        {showAlertSettings && (
          <div className="absolute inset-0 z-50">
            <AlertSettings />
          </div>
        )}
      </div>
    </>
  );
}
